import { Delete, Edit } from '@mui/icons-material';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { type JSX, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import type { QuickFilterProps } from '~/components/common/QuickFilterInput';
import { AIIcon } from '~/components/icons';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable, LiftAIToolbarProps } from '~/components/table';
import type { TicketExpandableRowProps } from '~/components/table/expandableRow';
import { TICKET_DETAIL_PANEL_HEIGHT, TicketExpandableRow } from '~/components/table/expandableRow';
import { useGridViews } from '~/components/table/views/useGridViews';
import { useDeleteEntityDialog } from '~/components/tickets';
import { TicketDocumentFromList } from '~/components/tickets/TicketDocumentFromList';
import { TicketPreviewPanel } from '~/components/tickets/TicketPreviewPanel';
import AddTicketDialog from '~/components/uploadDocument/AddTicketDialog';
import { mapTicketToFormValues } from '~/components/uploadDocument/TicketForm';
import useTicket from '~/data/hooks/useTicket';
import { isTicketAutoUpload, type Ticket, type TicketWithPortfolio } from '~/data/schemas/ticket';
import { useGridExporter } from '~/hooks/useGridExporter';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import {
  ACTIONS_ON_TICKETS,
  canAccess,
  DOWNLOAD_TICKET_FILE,
  NAVIGATE_TO_PROPERTY_PAGE,
} from '~/utils/role';

import { getTicketsTableColumns } from './ticketTableColumns';

const defaultSortModel: GridSortModel = [{ field: 'startedTime', sort: 'desc' }];

export type TicketTableProps = {
  previewList?: 'tickets' | 'property_tickets';
  tickets: TicketWithPortfolio[];
  numTickets: number;
  isLoading: boolean;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onFilterModelChange: (model: GridFilterModel) => void;
  onExport: () => Promise<TicketWithPortfolio[]>;
  paginationModel: GridPaginationModel;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  hiddenColumns?: string[];
  /**
   * Which set of saved views this table reads and writes. A mount point showing a narrower
   * table passes its own, or one list's saved view would rearrange the other.
   */
  viewsStoreKey?: string;
  /** Drops this page's filter query params, for a page that reads them back when it fetches. */
  onClearUrlFilters?: () => void;
  enableQuickFilter?: boolean;
  quickFilterProps?: QuickFilterProps;
  canFilterProperty?: boolean;
  /** Rendered under the toolbar, for a scope the page applied that no column can show. */
  scopeContent?: ReactNode;
  /** Bounds the grid to this height so it scrolls internally instead of growing the page. */
  height?: number | string;
};

export function TicketTable({
  tickets,
  numTickets,
  isLoading,
  onExport,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  paginationModel,
  filterModel,
  sortModel,
  hiddenColumns = [],
  viewsStoreKey = 'tickets-table-columns-visibility',
  onClearUrlFilters,
  enableQuickFilter,
  quickFilterProps,
  canFilterProperty = true,
  scopeContent,
  height,
  previewList = 'tickets',
}: TicketTableProps) {
  const { showEntityActionSnackbar } = useLAISnackbar();
  const [gridAction, setGridAction] = useState<{
    actionName: 'actionEdit' | 'actionDelete';
    ticketId: string;
  } | null>(null);
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({
    list: previewList,
    apiRef,
    scope: previewList === 'tickets' ? 'page' : 'table',
  });

  const { openDocument } = preview.rowPreview;
  const {
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const terms = useLocaleTerms();

  const { ticket: ticketToEdit, deleteTicket } = useTicket(gridAction ? gridAction.ticketId : null);
  const ticketFormData = useMemo(
    () => (ticketToEdit ? mapTicketToFormValues(ticketToEdit) : null),
    [ticketToEdit],
  );

  const clearGridAction = useCallback(() => {
    setGridAction(null);
  }, [setGridAction]);

  const { dialog: deleteDialog, openForEntity: openDeleteDialogForTicket } = useDeleteEntityDialog({
    onDelete: async (entity: Ticket) => {
      await deleteTicket(entity.id);

      showEntityActionSnackbar(
        {
          name: 'Ticket',
          action: ActionEnum.Delete,
          id: entity.number,
        },
        {
          variant: 'info',
        },
      );
    },
    entityName: 'Ticket',
  });

  const getFilename = useCallback(() => {
    return `liftai-tickets-${new Date().toJSON()}`;
  }, []);
  const { GridToolbarExport } = useGridExporter<TicketWithPortfolio>({
    fetcher: onExport,
    apiRef,
    getFilename,
  });

  const { columnVisibilityModel, views, resetFilters } = useGridViews({
    apiRef,
    storeKey: viewsStoreKey,
    initialVisibility: {
      id: false,
      finishedTime: false,
      callTime: false,
      timeOnSiteRegularHours: false,
      timeOnSiteOvertime: false,
      travelTimeRegularHours: false,
      travelTimeOvertime: false,
      ...Object.fromEntries(hiddenColumns.map((col) => [col, false])),
    },
  });

  const getActionsEl = useCallback(
    (ticket: Ticket): JSX.Element[] => {
      const actions: JSX.Element[] = [];

      if (isTicketAutoUpload(ticket)) {
        actions.push(
          <GridActionsCellItem
            key="processed-by-ai"
            title="This ticket was processed by AI and may require verification. Please review for accuracy."
            label="processed by AI"
            icon={<AIIcon />}
            disabled
            // pointerEvents allow the tooltip to show on hover
            style={{ pointerEvents: 'all', padding: 0 }}
          />,
        );
      }

      actions.push(
        ...[
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            onClick={() => {
              if (!ticket.id) {
                console.error('Ticket has no ID');
                return;
              }
              setGridAction({ actionName: 'actionEdit', ticketId: ticket.id });
            }}
            label="Edit"
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            showInMenu
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              openDeleteDialogForTicket(ticket);
            }}
          />,
        ],
      );

      return actions;
    },
    [openDeleteDialogForTicket, setGridAction],
  );

  const shouldLinkOnProperty = canAccess(userRole, NAVIGATE_TO_PROPERTY_PAGE);
  const canEdit = canAccess(userRole, ACTIONS_ON_TICKETS);
  const ticketListCols = useMemo(
    () =>
      getTicketsTableColumns({
        linkOnProperty: shouldLinkOnProperty,
        canEdit,
        getActionsEl,
        canFilterProperty,
        terms,
        onPreviewDocument:
          preview.enabled && canAccess(userRole, DOWNLOAD_TICKET_FILE)
            ? (row) => openDocument(row.id)
            : undefined,
      }),
    [
      shouldLinkOnProperty,
      canEdit,
      getActionsEl,
      canFilterProperty,
      terms,
      preview.enabled,
      openDocument,
      userRole,
    ],
  );

  const expandedRowId = searchParams.get('ticketId');

  const initialState = {
    pinnedColumns: { right: ['actions'] },
    columns: {
      columnVisibilityModel,
    },
    sorting: {
      sortModel: defaultSortModel,
    },
    detailPanel: expandedRowId
      ? {
          expandedRowIds: [expandedRowId],
        }
      : undefined,
  };

  const toolbarProps: LiftAIToolbarProps = useMemo(
    () => ({
      CustomGridToolbarExport: GridToolbarExport,
      views,
      resetFilters,
      onClearUrlFilters,
      scopeContent,
      filterControls: preview.peekSwitch,
    }),
    [GridToolbarExport, views, resetFilters, onClearUrlFilters, scopeContent, preview.peekSwitch],
  );

  return (
    <>
      <LiftAITable<TicketWithPortfolio>
        rows={tickets}
        apiRef={apiRef}
        rowCount={numTickets}
        loading={isLoading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        filterMode="server"
        filterModel={filterModel}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        toolbarProps={toolbarProps}
        onFilterModelChange={onFilterModelChange}
        columns={ticketListCols}
        getDetailPanelContent={({ row }: TicketExpandableRowProps<TicketWithPortfolio>) => {
          return <TicketExpandableRow row={row} />;
        }}
        // Sizes the detail panel to its content instead of MUI's 500px default.
        getDetailPanelHeight={() => TICKET_DETAIL_PANEL_HEIGHT}
        tableHeaderTitle={'Tickets'}
        initialState={initialState}
        enableQuickFilter={enableQuickFilter}
        quickFilterProps={quickFilterProps}
        height={height}
        {...preview.tableProps}
      />
      {preview.enabled ? <TicketPreviewPanel rowPreview={preview.rowPreview} /> : null}
      {preview.rowPreview.documentId ? (
        <TicketDocumentFromList
          ticketId={preview.rowPreview.documentId}
          onClose={preview.rowPreview.closeDocument}
        />
      ) : null}
      {ticketFormData && canAccess(userRole, ACTIONS_ON_TICKETS) ? (
        <AddTicketDialog
          open
          onClose={clearGridAction}
          onSubmit={clearGridAction}
          isEdit
          defaultValues={ticketFormData}
        />
      ) : null}

      {canAccess(userRole, ACTIONS_ON_TICKETS) && deleteDialog}
    </>
  );
}
